import { Link } from "react-router-dom";
import { ShieldCheck, BookOpen, Users, RefreshCw, AlertTriangle, Mail } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { brand, founder } from "@/data/site/brand";

const principles = [
  {
    icon: ShieldCheck,
    title: "Credentialed authorship and review",
    body: `Every page about a tax matter is written or reviewed by ${founder.name}, ${founder.credentials} — a federally authorized CPA with 15+ years representing taxpayers before the IRS. The reviewer's name, credentials, and the date of the most recent review appear at the top of each article.`,
  },
  {
    icon: BookOpen,
    title: "Primary sources first",
    body: "We cite the IRS (irs.gov), the Internal Revenue Code, the Internal Revenue Manual, U.S. Tax Court opinions, and Treasury regulations directly. When we summarize a notice, form, or procedure, we link to the official source so readers can verify it themselves.",
  },
  {
    icon: Users,
    title: "Real-world practice context",
    body: "Where useful, we add anonymized practice context — what we actually see in IRS field collection, examination, and appeals offices. Names, balances, and identifying details are changed or aggregated. We never publish a client matter without written authorization.",
  },
  {
    icon: RefreshCw,
    title: "Dated at publication",
    body: "Each article shows the date it was written or last reviewed at the top of the page. Tax law, IRS procedures, dollar thresholds, and forms change frequently — we do not promise that every article reflects current law at the moment you read it. Always confirm current rules with a CPA before acting on anything you read here.",
  },
  {
    icon: AlertTriangle,
    title: "Corrections policy",
    body: "If you find an error, let us know through our contact page or by calling the office. When we correct a factual error, we update the page, refresh the review date, and — for substantive changes — note what changed.",
  },
  {
    icon: Mail,
    title: "Editorial independence",
    body: "Our content is not sponsored, syndicated, or paid for by any third party. We do not accept guest posts, link insertions, or paid placements. The only commercial relationship described on this site is the engagement between our firm and our clients.",
  },
];

const notIncluded = [
  "Legal advice. We are a CPA firm, not a law firm. For litigation or non-tax legal advice, consult a licensed attorney.",
  "Investment, financial-planning, or insurance recommendations.",
  "Guarantees of specific IRS outcomes. Results depend on each taxpayer's facts and circumstances.",
  "AI-generated content published without CPA review. Drafting tools may assist research, but every published page is reviewed by a credentialed human.",
];

const EditorialStandards = () => (
  <SiteLayout>
    <PageSEO
      title="Editorial Standards & Review Policy"
      description={`How ${brand.shortName} researches, writes, reviews, sources, and corrects every page on this site — written and reviewed by a credentialed CPA.`}
      path="/editorial-standards"
      image={stockImages.accountingDesk.src}
      breadcrumbs={[
        { name: "Home", href: "/" },
        { name: "Editorial Standards", href: "/editorial-standards" },
      ]}
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "Editorial Standards & Review Policy",
        url: `${brand.siteUrl}/editorial-standards`,
        about: {
          "@type": "Organization",
          name: brand.legalName,
          url: brand.siteUrl,
        },
        mainEntity: {
          "@type": "Person",
          name: founder.name,
          jobTitle: founder.title,
          url: `${brand.siteUrl}/about/franklin-sofi`,
        },
      }}
    />

    <section className="relative bg-gradient-navy text-white overflow-hidden">
      <HeroBackground image={stockImages.accountingDesk} priority />
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
        <Breadcrumbs
          items={[
            { name: "Home", href: "/" },
            { name: "Editorial Standards" },
          ]}
          tone="dark"
        />
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mt-2 mb-4">
          How we work
        </p>
        <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
          Editorial standards & review policy.
        </h1>
        <p className="text-lg text-white/80 max-w-2xl leading-relaxed">
          Tax content can change the outcome of a real IRS case. Here's how we
          decide what to publish, who reviews it, where we get our facts, and
          how we fix mistakes.
        </p>
      </div>
    </section>

    <section className="py-16 lg:py-32 bg-white">
      <div className="container mx-auto px-4 max-w-4xl space-y-10">
        {principles.map((p) => {
          const Icon = p.icon;
          return (
            <div key={p.title} className="grid md:grid-cols-[auto_1fr] gap-5 md:gap-8">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-secondary text-navy">
                <Icon className="w-6 h-6 text-gold" />
              </div>
              <div>
                <h2 className="font-heading text-xl lg:text-2xl font-bold text-navy mb-2 leading-tight">
                  {p.title}
                </h2>
                <p className="text-foreground/85 leading-[1.75]">{p.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>

    <section className="py-20 bg-secondary/40 border-y border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">
          What we don't publish
        </p>
        <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy mb-6 leading-[1.2]">
          The lines we don't cross.
        </h2>
        <ul className="space-y-3">
          {notIncluded.map((n) => (
            <li key={n} className="flex gap-3 text-foreground/85 leading-relaxed">
              <span className="text-gold font-bold mt-1">—</span>
              <span>{n}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 max-w-4xl">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">
          Who reviews this content
        </p>
        <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy mb-6 leading-[1.2]">
          The reviewer.
        </h2>
        <div className="flex flex-col sm:flex-row items-start gap-6 border border-border rounded-lg p-6 bg-secondary/30">
          <img
            src={founder.image}
            alt={founder.name}
            className="w-24 h-24 rounded-full object-cover object-top border-2 border-gold shrink-0"
           width={96} height={96} />
          <div className="min-w-0">
            <h3 className="font-heading text-xl font-bold text-navy">
              {founder.name}, {founder.credentials}
            </h3>
            <p className="text-sm text-muted-foreground mb-3">{founder.title}</p>
            <p className="text-foreground/85 leading-relaxed mb-4">{founder.bio}</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <Link to="/about/franklin-sofi" className="font-semibold text-navy hover:text-gold">
                Full bio →
              </Link>
              {founder.verifyLinks.map((v) => (
                <a
                  key={v.href}
                  href={v.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-navy hover:text-gold"
                >
                  {v.org} ↗
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    <CTASection />
  </SiteLayout>
);

export default EditorialStandards;
